import * as runApi from '@/api/runs';
import { boundedOrderRead } from '@/hooks/useOrderWorkspaceSession';

const terminal = item => ['completed', 'refused'].includes(item?.state);
const canonical = value => JSON.stringify(value, (_, next) => next && typeof next === 'object' && !Array.isArray(next)
  ? Object.fromEntries(Object.keys(next).sort().map(key => [key, next[key]])) : next);
const envelopeOf = item => item?.cloud_request || item?.envelope;
const valid = item => {
  const envelope = envelopeOf(item);
  return item && typeof item.attempt_id === 'string' && item.attempt_id
    && ['pending', 'accepted_local_pending', 'completed', 'refused'].includes(item.state)
    && ['draft', 'cached_proposal'].includes(item.source_kind) && typeof item.source_id === 'string'
    && typeof item.work_epoch === 'string' && !!item.work_epoch && envelope?.schema_version === 1
    && envelope.attempt_id === item.attempt_id && envelope.work_epoch === item.work_epoch
    && envelope.source?.kind === item.source_kind && envelope.source?.id === item.source_id
    && envelope.operation === item.operation && ['replay', 'save_as_new', 'reconcile'].includes(item.operation)
    && typeof envelope.tenant_id === 'string' && !!envelope.tenant_id
    && envelope.payload && typeof envelope.payload === 'object' && !Array.isArray(envelope.payload);
};
const binding = response => {
  const value = response.headers?.get?.('X-LVB-Cache-Binding') ?? response.headers?.['x-lvb-cache-binding'];
  if (typeof value !== 'string' || !/^[a-f0-9]{64}$/.test(value)) throw new Error('Run recovery cache identity is unavailable');
  return value;
};
const assert = condition => { if (!condition) throw new Error('Run recovery context changed'); };

export const runSendHold = run => run?.run_send_hold || run?.offline_proposal?.run_send_hold || null;
export const RUN_SEND_HOLD_MESSAGE = 'A previous send needs checking in Review. This local plan is protected until its outcome is known.';

// Durable authority belongs to the backend. This owner contains only observation
// and request ownership; no token, intent or outcome is saved to browser storage.
export function createRunSendRecovery(session, { api = runApi, changed = () => {}, settled = () => {} } = {}) {
  let active = false;
  let generation = 0;
  let items = [];
  let error = '';
  let loading = false;
  let nextCursor = null;
  const busy = new Set();
  const preparing = new Set();
  const messages = new Map();
  const failureMessage = id => {
    const state = items.find(item => item.attempt_id === id)?.outcome?.state;
    if (state === 'accepted') return 'Online acceptance is confirmed. Local recording still needs to finish; do not send it again.';
    if (state === 'refused') return 'No online change is confirmed. Finish local recording before editing this plan.';
    return 'The outcome could not be confirmed. This plan remains protected; no automatic repeat was sent.';
  };
  const controllers = new Set();
  const read = (execute, config) => boundedOrderRead(execute, config, controllers);
  const snapshot = () => ({ items, error, loading, nextCursor, busy: new Set(busy), messages: new Map(messages) });
  const emit = () => { if (active) changed(snapshot()); };
  const retainEvidence = item => {
    const previous = items.find(value => value.attempt_id === item.attempt_id && value.work_epoch === item.work_epoch
      && canonical(envelopeOf(value)) === canonical(envelopeOf(item)));
    if (!previous || terminal(item)) return item;
    if (terminal(previous)) return previous;
    return !item.outcome && previous.outcome ? { ...item, state: previous.state, outcome: previous.outcome } : item;
  };
  const put = item => {
    assert(valid(item));
    items = [...items.filter(value => value.attempt_id !== item.attempt_id), retainEvidence(item)];
    emit();
  };
  const capture = () => {
    const local = session.isAdmin && session.capture(true);
    if (!active || !local) return null;
    const turn = generation;
    const cloud = !session.offline && session.capture(false);
    const current = () => active && turn === generation && local.current();
    return { current, tenant: local.tenant, cloud: cloud && { ...cloud, current: () => current() && cloud.current() },
      local: { ...local.config, reviewLocalBase: local.endpoint.replace(/\/api$/, '') } };
  };
  const readOne = async (id, scope) => {
    let tag;
    assert(scope.current());
    const item = await read(config => api.getRunSendAttempt(id, config), { ...scope.local, reviewResponse: response => { tag = binding(response); } });
    assert(scope.current() && valid(item) && envelopeOf(item).tenant_id === scope.tenant && item.attempt_id === id && tag);
    return { item, config: { ...scope.local, headers: { ...scope.local.headers, 'X-LVB-Cache-Binding': tag } } };
  };
  const finish = async (item, config, scope, outcome) => {
    assert(scope.current());
    if (outcome) {
      assert(outcome.schema_version === 1 && outcome.attempt_id === item.attempt_id
        && ['accepted', 'refused'].includes(outcome.state) && typeof outcome.request_digest === 'string'
        && canonical(outcome.envelope) === canonical(envelopeOf(item)));
      put({ ...item, state: outcome.state === 'accepted' ? 'accepted_local_pending' : 'pending', outcome });
    }
    const value = outcome ? await api.recordRunSendOutcome(item.attempt_id, outcome, config)
      : await api.settleRunSend(item.attempt_id, config);
    assert(scope.current() && valid(value) && value.attempt_id === item.attempt_id
      && value.work_epoch === item.work_epoch && canonical(envelopeOf(value)) === canonical(envelopeOf(item)));
    put(value);
    if (terminal(value)) settled(value);
    return value;
  };
  const recover = async (id, mode = 'check') => {
    const scope = capture();
    if (!scope || busy.has(id)) return null;
    busy.add(id); messages.delete(id); emit();
    try {
      const { item, config } = await readOne(id, scope);
      const observed = items.find(value => value.attempt_id === id);
      assert(!observed || (observed.work_epoch === item.work_epoch && canonical(envelopeOf(observed)) === canonical(envelopeOf(item))));
      const known = items.find(value => value.attempt_id === id && value.work_epoch === item.work_epoch
        && canonical(envelopeOf(value)) === canonical(envelopeOf(item)) && ['accepted', 'refused'].includes(value.outcome?.state));
      put(item);
      if (terminal(item)) return item;
      if (known && !item.outcome) return await finish(item, config, scope, known.outcome);
      // The server has already validated this receipt; no cloud availability or
      // round trip is needed to finish its bookkeeping.
      if (item.state === 'accepted_local_pending' || ['accepted', 'refused'].includes(item.outcome?.state)) {
        return await finish(item, config, scope);
      }
      if (mode === 'finish') return item;
      if (!scope.cloud?.current()) {
        messages.set(id, 'Waiting for a connection and current online permission to check the previous send.'); return item;
      }
      const envelope = envelopeOf(item);
      const outcome = mode === 'retry' ? await api.sendRunAttempt(envelope, scope.cloud.config)
        : mode === 'release' ? await api.closeRunSendAttempt(id, envelope, scope.cloud.config)
          : await read(config => api.getRunSendOutcome(id, config), scope.cloud.config);
      assert(scope.current() && scope.cloud.current());
      if (outcome?.state === 'unknown' && outcome?.attempt_id === id) {
        messages.set(id, 'The previous send is still unconfirmed. Checking did not send it again.'); return item;
      }
      return await finish(item, config, scope, outcome);
    } catch {
      if (scope.current()) messages.set(id, failureMessage(id));
      return null;
    } finally { busy.delete(id); emit(); }
  };
  const owner = {
    activate() { active = true; },
    retire() { active = false; generation++; controllers.forEach(controller => controller.abort()); },
    snapshot,
    held(kind, id) { return items.some(item => !terminal(item) && item.source_kind === kind && item.source_id === id); },
    async load(more = false) {
      const scope = capture();
      if (!scope || loading) return false;
      loading = true; error = ''; emit();
      try {
        let tag;
        const page = await read(config => api.getRunSendAttempts(more ? nextCursor : null, config),
          { ...scope.local, reviewResponse: response => { tag = binding(response); } });
        assert(scope.current() && tag && Array.isArray(page?.items) && page.items.every(item => valid(item) && envelopeOf(item).tenant_id === scope.tenant)
          && (page.next_cursor === null || typeof page.next_cursor === 'string'));
        const observations = page.items.map(retainEvidence);
        items = more ? [...items.filter(item => !page.items.some(next => next.attempt_id === item.attempt_id)), ...observations] : observations;
        nextCursor = page.next_cursor;
        emit();
        // Bounded read recovery only. Additional rows remain explicit in the UI;
        // neither a page boundary nor lookup absence grants edit permission.
        await Promise.all(page.items.filter(item => !terminal(item)).slice(0, 5).map(item => recover(item.attempt_id)));
        return true;
      } catch {
        if (scope.current()) error = 'Saved run-send status is unavailable. No earlier send has been assumed complete.';
        return false;
      } finally { loading = false; emit(); }
    },
    check: id => recover(id),
    finish: id => recover(id, 'finish'),
    retry: id => recover(id, 'retry'),
    release: id => recover(id, 'release'),
    async start(context, request) {
      const key = `${request.source_kind}:${request.source_id}`;
      if (!active || !context?.dispatchCurrent() || preparing.has(key) || owner.held(request.source_kind, request.source_id)) return null;
      preparing.add(key);
      let prepared;
      let config = context.local;
      try {
        let tag;
        prepared = await api.prepareRunSend(request, { ...config, reviewResponse: response => { tag = binding(response); } });
        assert(context.current() && valid(prepared) && envelopeOf(prepared).tenant_id === context.tenant && tag && prepared.source_kind === request.source_kind
          && prepared.source_id === request.source_id && prepared.operation === request.operation);
        config = { ...config, headers: { ...config.headers, 'X-LVB-Cache-Binding': tag } };
        if (active) put(prepared);
        if (terminal(prepared)) return prepared;
        if (prepared.state === 'accepted_local_pending' || ['accepted', 'refused'].includes(prepared.outcome?.state)) {
          return await finish(prepared, config, { current: context.current });
        }
        // Navigation/session retirement after preparation leaves a saved hold;
        // it is never permission for a later hidden cloud dispatch.
        assert(active && context.dispatchCurrent());
        busy.add(prepared.attempt_id); emit();
        const outcome = await api.sendRunAttempt(envelopeOf(prepared), context.cloud);
        assert(context.current());
        const result = await finish(prepared, config, { current: context.current }, outcome);
        return result;
      } catch {
        if (active && context.current()) {
          if (prepared?.attempt_id) messages.set(prepared.attempt_id, failureMessage(prepared.attempt_id));
          else error = 'The run send was not prepared. Refresh this plan before trying again; no cloud send was started.';
          emit();
        }
        return null;
      } finally { preparing.delete(key); if (prepared) busy.delete(prepared.attempt_id); emit(); }
    },
  };
  return owner;
}
